import { CheckIcon, PencilIcon, XIcon } from "@heroicons/react/outline";
import CommentsPopup from "components/lib/Global/CommentsPopup";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import ReactTable from "components/lib/Shared/ReactTable";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addComment,
  editComment,
  deleteComment,
  getComments,
} from "state/slices/comment";
import { invalidateFields, resolveFields } from "state/slices/resolveField";
import _get from "lodash/get";
import CommentsList from "components/lib/Global/CommentsList";

const MinuteBook = ({ transactions, isViewMode }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const { registerOfApplicationsAndAllotments } = useSelector(
    (state) => state.minuteBookSlice
  );
  const { comments } = useSelector((state) => state.commentsSlice);
  const minuteBookComments = comments.filter(
    (comment) => comment.formName === "minuteBook"
  );
  const { company } = useSelector((state) => state.companyDashboardSlice);

  const dispatch = useDispatch();

  const tableData = [];

  const getMinuteBookEntries = () => {
    let entries = [];

    // Helper function to find the last (highest) value from an array of objects
    const findLastValue = (items, field) => {
      if (!items || items.length === 0) return 0;
      const values = items
        .map((item) => item.currentAllottedClass?.[field])
        .filter((val) => val !== undefined && val !== null)
        .map((val) => Number(val));
      return values.length > 0 ? Math.max(...values) : 0;
    };

    // Get the starting point for allotment and certificate numbers
    const lastAllotmentNumber = findLastValue(
      registerOfApplicationsAndAllotments.items,
      "numberOfAllotmentLetter"
    );
    const lastShareCertNumber = findLastValue(
      registerOfApplicationsAndAllotments.items,
      "shareCertificateNumber"
    );

    transactions.forEach((transaction) => {
      // Check if this transaction should show minute book entries
      const shouldShowMinuteBook = [
        "privateWithMoreThanOneClass",
        "unallotedShares",
        "PVTwithOneClass",
        "existingAuth",
        "shareTransfer",
      ].includes(transaction.processType);

      if (shouldShowMinuteBook && transaction.shareCapitalChanges) {
        const transactionEntries = transaction?.minuteBookDetails?.entries;
        if (transactionEntries && transactionEntries?.length > 0) {
          const mappedEntries = transactionEntries.map((entry) => ({
            ...entry,
            ...transaction,
          }));
          entries = [...entries, ...mappedEntries];
        } else {
          transaction.shareCapitalChanges.forEach((shareChange) => {
            if (shareChange.allocations) {
              shareChange.allocations.forEach((allocation) => {
                // Find the matching official in registerOfApplicationsAndAllotments
                const matchingOfficial =
                  registerOfApplicationsAndAllotments.items?.find(
                    (official) => official._id === allocation.officialId
                  );

                if (matchingOfficial) {
                  // Get full name based on official type
                  let fullName = "";
                  if (matchingOfficial.names.firstName) {
                    // Individual official
                    const {
                      firstName,
                      middleName = "",
                      lastName,
                    } = matchingOfficial.names;
                    fullName = `${firstName} ${middleName} ${lastName}`.trim();
                  } else if (matchingOfficial.names.companyName) {
                    // Corporate official
                    fullName = matchingOfficial.names.companyName;
                  }

                  entries.push({
                    fullName: fullName || "N/A",
                    classOfShares: shareChange.classOfShares || "Ordinary",
                    numberOfShares: allocation.allocatedShares || 0,
                    folioNo:
                      matchingOfficial.memberRegisterFolio ||
                      `${entries.length + 1}a`,
                    // Temporary values - will be assigned after sorting
                    allotmentNo: 0,
                    shareCertNo: 0,
                    sn: 0,
                    ...transaction,
                  });
                }
              });
            }
          });
        }
      }
    });

    // If no entries found, return empty array
    if (entries.length === 0) {
      return [];
    }

    // Objective 1 & 2: Sort entries according to priority rules
    const getClassPriority = (classOfShares) => {
      const normalizedClass = (classOfShares || "").toLowerCase();
      if (normalizedClass.includes("ordinary")) return 1; // Ordinary Shares first
      if (normalizedClass.includes("preference")) return 2; // Preference Shares second
      return 3; // Others last
    };

    const sortedEntries = entries.sort((a, b) => {
      // First priority: Class of shares (Ordinary -> Preference -> Others)
      const classComparison =
        getClassPriority(a.classOfShares) - getClassPriority(b.classOfShares);
      if (classComparison !== 0) return classComparison;

      // Second priority: Highest allocated shares first (descending)
      const sharesComparison = b.numberOfShares - a.numberOfShares;
      if (sharesComparison !== 0) return sharesComparison;

      // Third priority: Alphabetical by full name (ascending)
      return a.fullName.localeCompare(b.fullName);
    });
    // Assign serial numbers, allotment numbers, and certificate numbers after sorting
    let serialNumber = 1;
    let allotmentCounter = 1;
    let certCounter = 1;

    const mappedEntries = sortedEntries.map((entry, index) => ({
      sn: entry?.sn ?? serialNumber++,
      allotmentNo:
        entry?.allotmentNo ?? lastAllotmentNumber + allotmentCounter++,
      shareCertNo: entry?.shareCertNo ?? lastShareCertNumber + certCounter++,
      ...entry,
    }));

    return mappedEntries;
  };

  const minuteBookEntries = React.useMemo(
    () => getMinuteBookEntries(),
    // Recompute when transactions change OR when registerOfApplicationsAndAllotments updates from the store
    [transactions, registerOfApplicationsAndAllotments]
  );
  const handleSaveComment = async (data, row) => {
    if (data?.commentId) {
      await dispatch(editComment({ commentId: data.commentId, data }));
    } else {
      await dispatch(
        addComment({
          data: {
            ...data,
            company: company._id,
            transaction: row.original._id,
          },
        })
      );
    }
  };

  // ... (keep existing resolve/invalidate field handlers unchanged)
  const handleResolveBtnClicked = async (row) => {
    setIsLoading(true);
    setSelectedRow(row);
    const { original } = row;
    const commentsToBeResolved = comments?.filter(
      (comment) =>
        comment.formName === "minuteBook" &&
        comment.transaction === original?._id &&
        !comment.isResolved
    );
    const commentsToBeResolvedIds =
      commentsToBeResolved?.map(({ _id }) => _id) ?? [];
    const sliceIndex = transactions.findIndex(
      (trx) => trx._id === original._id
    );
    const promise = await dispatch(
      resolveFields({
        data: {
          formId: _get(original, "_id"),
          formName: "initiateTransactionChange",
          fieldName: original.changeType,
          resolvedFields: [original?.folioNo],
          commentsToBeResolvedIds,
        },
        updateEffectKey: "updateTransactionUnderReview",
        updateEffectPath: `transactionSlice.transactionUnderReview.items.transactions.${sliceIndex}`,
      })
    );
    if (commentsToBeResolvedIds.length > 0) {
      const query = { companyId: commentsToBeResolved[0]?.company };
      // if (isReviewerMode) query.createdBy = currentUser?._id;
      await dispatch(getComments(query));
    }

    setIsLoading(false);
    setSelectedRow({});
  };

  const handleInvalidateBtnClicked = async (row) => {
    setIsLoading(true);
    setSelectedRow(row);
    const { original } = row;
    const sliceIndex = transactions.findIndex(
      (trx) => trx._id === original._id
    );
    const commentsToBeResolved = comments?.filter(
      (comment) =>
        comment.formName === "minuteBook" &&
        comment.transaction === original?._id &&
        !comment.isResolved
    );
    const commentsToBeResolvedIds =
      commentsToBeResolved?.map(({ _id }) => _id) ?? [];
    const promise = await dispatch(
      invalidateFields({
        data: {
          formId: _get(original, "_id"),
          formName: "initiateTransactionChange",
          fieldName: original.changeType,
          invalidFields: [original?.folioNo],
          commentsToBeResolvedIds,
        },
        updateEffectKey: "updateTransactionUnderReview",
        updateEffectPath: `transactionSlice.transactionUnderReview.items.transactions.${sliceIndex}`,
      })
    );
    setIsLoading(false);
    setSelectedRow({});
  };
  const columns = [
    {
      Header: "Full Name",
      accessor: "fullName",
    },
    {
      Header: "Class of Shares",
      accessor: "classOfShares",
    },
    {
      Header: "No. of Shares",
      accessor: "numberOfShares",
    },
    {
      Header: "Allotment No.",
      accessor: "allotmentNo",
    },
    {
      Header: "Folio No.",
      accessor: "folioNo",
    },
    {
      Header: "Share Cert No.",
      accessor: "shareCertNo",
    },
  ];
  if (!isViewMode) {
    columns.push({
      Header: "Actions",
      Cell: ({ row }) => {
        const { original } = row;
        const { resolvedFields } = original;
        return (
          <div className="flex items-center space-x-2">
            {!resolvedFields.includes(original?.changeType) && (
              <CommentsPopup
                handleSaveComment={(data) => {
                  handleSaveComment(data, row);
                }}
                formName="minuteBook"
                fieldName={original.applicationType}
                fieldLabel={original.changeType}
                comment={{}}
              >
                <span>Review</span>
                <PencilIcon className="inline w-4 h-4" />
              </CommentsPopup>
            )}
            <div className={`flex items-center justify-center`}>
              {resolvedFields.includes(original?.folioNo) ? (
                <button
                  className={`flex space-x-1 ${
                    isViewMode ? "cursor-not-allowed" : "cursor-pointer"
                  }`}
                  onClick={() =>
                    isViewMode ? null : handleInvalidateBtnClicked(row)
                  }
                >
                  {isLoading && selectedRow.index === row.index ? (
                    <SpinnerIcon className="text-primary" />
                  ) : (
                    <>
                      <span>Accepted </span>
                      <XIcon className="inline w-4 h-4" />
                    </>
                  )}
                </button>
              ) : (
                <button
                  className={`flex space-x-1 ${
                    isViewMode ? "cursor-not-allowed" : "cursor-pointer"
                  }`}
                  onClick={() =>
                    isViewMode ? null : handleResolveBtnClicked(row)
                  }
                >
                  {isLoading && selectedRow.index === row.index ? (
                    <SpinnerIcon className="text-primary" />
                  ) : (
                    <>
                      <span>Accept</span>
                      <CheckIcon className="inline w-4 h-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        );
      },
    });
  }

  const nonRemainderEntries = minuteBookEntries.filter(
    (entry) => !entry?.isRemainder
  );

  const remainderEntries = minuteBookEntries.filter(
    (entry) => entry?.isRemainder
  );

  return (
    <div>
      {minuteBookComments.length > 0 && (
        <div className="px-8 pt-5 pb-6 mb-4 bg-white">
          <CommentsList
            comments={minuteBookComments}
            toggleCommentStatus={!isViewMode}
          />
          <p className="mt-6 text-sm text-primary">
            Comments: {minuteBookComments.length} of {minuteBookComments.length}
          </p>
        </div>
      )}
      {nonRemainderEntries?.length > 0 && (
        <div className="mt-6 w-full bg-white shadow overflow-x-auto">
          <ReactTable
            columns={columns}
            data={nonRemainderEntries || []}
            dataRowCellStyle="pl-4 leading-snug py-2"
            // renderRowSubComponent={renderRowSubComponent}
            paginate={true}
            totalRows={10}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      )}

      {remainderEntries?.length > 0 && (
        <div className="mt-6 w-full bg-white shadow overflow-x-auto">
          <div className="px-4 py-2  bg-gray-100">Balance Shares</div>
          <ReactTable
            columns={columns}
            data={remainderEntries || []}
            dataRowCellStyle="pl-4 leading-snug py-2"
            // renderRowSubComponent={renderRowSubComponent}
            paginate={true}
            totalRows={10}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
};

export default MinuteBook;
